/**
 * v7 Personal Dashboard — 3-col grid for admin, 2-col for PM-only persona.
 *
 * Quick task 260512-uat-batch (2026-05-12) amendments:
 * - F-08 admin freelance Approvals gate: showApprovals dropped the
 *   `!isFreelance` clause for admins (admin role overrules employeeType).
 * - F-06 CashflowCard → InvoicesCard: Cashflow report wasn't built,
 *   placeholder card showed "—". Replaced with a CTA card linking to
 *   /reports/invoicing-status (the report that exists).
 * - F-05a HolidaysCard (admin-only) — Czech state holidays next 30 days.
 * - F-14 PM NeedsApprovalCard visibility — PMs (non-manager) now see
 *   approvals on dashboard. Backend per-PM scope for counts is wired
 *   server-side via useApprovalCounts.
 * - F-15 PM 2-col layout — PM-only persona (PM but not manager, not admin,
 *   not finance) collapses to 2 cols since Receivables / Cashflow /
 *   ISMS-style cards are not visible.
 *
 * Region structure (top → bottom):
 *   1. Header row: greeting + date subtitle + inline "Log hours" button.
 *   2. KPI strip: 4 horizontal tiles (last-month — Hours / Approved
 *      Expenses / Invoiced w/ sent indicator / Time Off days remaining).
 *   3. Hero row: CurrentMonthTimesheetCard + HolidaysCard (admin) /
 *      HelpdeskCard (everyone else — MKS-339). Behind
 *      `mks_392_upcoming_celebrations` the row is 3-up for everyone:
 *      CurrentMonthTimesheetCard + HelpdeskCard + the Upcoming card.
 *   4. Body grid:
 *      - Admin (3 cols): TimeOff+Receivables | NeedsApproval | Invoices+AsPM
 *      - PM-only (2 cols): TimeOff+AsPM | NeedsApproval
 *      - Plain user (1 col / responsive): TimeOff only
 *   5. Bottom row: MyExpensesCard + RecentTimesheetsCard.
 *
 * Impersonation guard (Phase 27 D-13 / Phase 41 Pitfall 5) preserved —
 * every admin-sensitive flag is AND'd with `!isImpersonating`.
 *
 * F-22 (2026-05-13 UAT batch Phase 3): isPM now comes from `auth.isPM`
 * (backed by GET /api/auth/me — single source of truth). The prior I-23
 * skeleton workaround for isPM flicker (false → true while useMyProjects
 * loaded) is gone, because isPM resolves synchronously with auth.user.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/authContext';
import { useFlag } from '../services/flagsContext';
import { Btn } from '../components/ui/Btn';
import { KpiStrip } from '../components/dashboard/KpiStrip';
import { CurrentMonthTimesheetCard } from '../components/dashboard/CurrentMonthTimesheetCard';
import { TimeOffSummaryCard } from '../components/dashboard/TimeOffSummaryCard';
import { NeedsApprovalCard } from '../components/dashboard/NeedsApprovalCard';
import { NeedsAttentionCard } from '../components/dashboard/NeedsAttentionCard';
import { InvoicesCard } from '../components/dashboard/InvoicesCard';
import { ReceivablesCard } from '../components/dashboard/ReceivablesCard';
import { AsProjectManagerCard } from '../components/dashboard/AsProjectManagerCard';
import { HelpdeskCard } from '../components/dashboard/HelpdeskCard';
import { MyExpensesCard } from '../components/dashboard/MyExpensesCard';
import { RecentTimesheetsCard } from '../components/dashboard/RecentTimesheetsCard';
import { HolidaysCard } from '../components/dashboard/HolidaysCard';
import { SpotlightCard } from '../components/respect/SpotlightCard';
import { formatDate, formatWeekdayLong } from '../utils/dateFormat';
import { ThemeEmoji } from '../components/ui/ThemeEmoji';

export function DashboardPage() {
  const { user, isAdmin, isFinance, isManager, isImpersonating, impersonatedUser, isPM } =
    useAuth();
  const navigate = useNavigate();
  const isFreelance = user?.employeeType?.name === 'Freelance';

  // F-22 (2026-05-13 UAT batch Phase 3) — isPM now comes from
  // `auth.isPM` (backed by GET /api/auth/me). The prior pattern of
  // `useMyProjects().some(p => p.projectManager?.id === user?.id)` was
  // broken because the memberOf=true response branch nulls `projectManager`
  // on sidebar rows AND hard-filters state='in_progress'. AsProjectManagerCard
  // still calls useMyProjects internally for the list it renders — that
  // hook is no longer mounted here, so the I-23 "myProjects loading
  // skeleton" is gone: isPM is now available synchronously the moment
  // `auth.user` resolves (before that, ProtectedRoute holds the redirect).

  // F-08 (2026-05-12) — admin role overrules employeeType. Petra (admin +
  // Freelance employeeType) was hidden from NeedsApprovalCard because the
  // prior gate ANDed `!isFreelance` regardless of role.
  //
  // F-14 (2026-05-12) — PMs (projectManagerId on at least one project)
  // also see NeedsApprovalCard; backend useApprovalCounts already scopes
  // counts to the PM's projects when the caller is not isManager.
  const showApprovals =
    !isImpersonating && (isAdmin || (isManager && !isFreelance) || isPM);
  const showAdminOnly = isAdmin && !isImpersonating; // HolidaysCard, InvoicesCard
  // MKS-329 — Outstanding invoices is a finance surface, not an admin-only one.
  // `isFinance` is the loose admin-or-finance flag.
  const showReceivables = isFinance && !isImpersonating;
  // MKS-392 — the Upcoming card (holidays + team name days, plus birthdays
  // for admin/HR — the card's own doc explains that split) is for everyone,
  // not just admins; with the flag off it stays the admin-only holidays card
  // in the admin's hero slot.
  const celebrationsOn = useFlag('mks_392_upcoming_celebrations');
  // F-15 PM-only persona — when the actor is PM but neither admin nor
  // manager, drop to 2 cols (TimeOff+AsPM | NeedsApproval) instead of 3.
  const isPMOnly = isPM && !isAdmin && !isManager;

  const greetingName = impersonatedUser?.firstName ?? user?.firstName ?? '';
  // MKS-351 — "MONDAY, 20.11.2026" from the shared util.
  const today = new Date();
  const todayLabel = `${formatWeekdayLong(today)}, ${formatDate(today)}`.toUpperCase();

  return (
    <div className="px-4 py-2 max-w-[1400px] mx-auto space-y-6">
      {/* Region 1 — Header row (greeting + date subtitle). Search/bell/+Log
          hours live in the Layout TopBar; the inline button below is
          preserved for backward compatibility with the 41-05 test contract. */}
      <header className="flex items-center justify-between">
        <div>
          <p className="uppercase text-[11px] tracking-wider text-ms-text-muted font-semibold">
            DASHBOARD · {todayLabel}
          </p>
          <h1 className="text-3xl font-semibold text-ms-text-strong mt-1">
            Hi, {greetingName}
            {/* MKS-493 — the welcome message carries the main-action emoji in
                Sparkle. Nothing renders in the default theme. */}
            <ThemeEmoji role="action" />
          </h1>
        </div>
        <Btn variant="accent" size="md" onClick={() => navigate('/time')}>
          Log hours
        </Btn>
      </header>

      {/* Region 2 — KPI strip */}
      <KpiStrip />

      {/* Region 3 — Hero row */}
      <div
        className={
          celebrationsOn
            ? 'grid grid-cols-1 lg:grid-cols-3 gap-6'
            : 'grid grid-cols-1 lg:grid-cols-2 gap-6'
        }
      >
        <CurrentMonthTimesheetCard />
        {/* MKS-339 — with the flag off, HelpdeskCard renders the Coming-soon
            placeholder this slot has always had; with it on, the real card,
            which hides itself for a viewer who handles no requests.
            MKS-392 — with `mks_392_upcoming_celebrations` on, the hero row
            carries BOTH: the Upcoming card is a team celebration everyone
            sees, and an admin no longer loses the Helpdesk card to it (which
            is the MKS-339 slot the holidays card used to take over). */}
        {celebrationsOn ? (
          <>
            <HelpdeskCard />
            <HolidaysCard />
          </>
        ) : showAdminOnly ? (
          <HolidaysCard />
        ) : (
          <HelpdeskCard />
        )}
      </div>

      {/* Region 4 — body grid (3-col admin, 2-col PM-only, 1-col plain user) */}
      <div
        className={
          isPMOnly
            ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
            : 'grid grid-cols-1 lg:grid-cols-3 gap-6'
        }
      >
        <div className="space-y-6">
          <TimeOffSummaryCard />
          {showReceivables && <ReceivablesCard />}
          {/* PM-only persona keeps AsProjectManagerCard in col 1 alongside
              TimeOff — col 2 carries NeedsApproval, no col 3. */}
          {isPMOnly && user && <AsProjectManagerCard />}
        </div>
        <div className="space-y-6">
          <NeedsAttentionCard />
          {showApprovals && <NeedsApprovalCard />}
        </div>
        {!isPMOnly && (
          <div className="space-y-6">
            {showAdminOnly && <InvoicesCard />}
            {isPM && user && <AsProjectManagerCard />}
          </div>
        )}
      </div>

      {/* Region 5 — Bottom row (3 cards: expenses + timesheets + respect spotlight) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <MyExpensesCard />
        <RecentTimesheetsCard />
        {/* Phase 61 — Spotlight: 3-5 spotlight-level users; no "see all" / leaderboard (D-03) */}
        <SpotlightCard />
      </div>
    </div>
  );
}
